/**
 * Modal de configuração para nodes de mensagem (texto e mídia)
 * Permite editar texto, mídia e configurações específicas
 *
 * RELEASE: 4.3.0 - 2026-08-29 - Nova origem de mídia "Variável do fluxo": permite
 *          usar variáveis interpoladas ({{minha_var_pdf_url}}) como URL do arquivo,
 *          sem alterar o comportamento de "URL Externa" e "Galeria de Mídia".
 * RELEASE: 4.2.0 - 2026-05-31 - Adicionado preview universal de mídia (galeria + URL externa) com lightbox por tipo, restaurando contexto da galeria ao reabrir o modal
 * RELEASE: 4.1.0 - 2026-01-25 - Adicionado seletor de canal de envio para suporte a gatilhos CRM
 */

import { useState, useEffect, useRef } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Separator } from '@/components/ui/separator';
import { FlowNode, MessageTextConfig, MessageMediaConfig } from '@/types/flows';
import { MediaGalleryItem } from '@/types/media';
import { z } from 'zod';
import { toast } from 'sonner';
import { HelpCircle, Link, Image, X, Send, MessageSquare, AlertTriangle, Braces } from 'lucide-react';
import { formatBytes } from '@/types/media';
import { NodeHelpDialog } from './NodeHelpDialog';
import { NodeIdField, NodeIdFieldRef } from '../NodeIdField';
import { MediaGalleryPicker } from '@/components/tenant/media/MediaGalleryPicker';
import { MediaConfigPreview } from './MediaConfigPreview';
import { TextareaWithVariables } from '../TextareaWithVariables';
import { InputWithVariables } from '../InputWithVariables';
import { getStorageUrl } from '@/lib/storage';
import { createLogger } from '@/lib/logger';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

// Schema de validação para mensagem de texto
const messageTextSchema = z.object({
  text: z.string().trim().min(1, 'O texto da mensagem é obrigatório').max(4096, 'Texto muito longo (máximo 4096 caracteres)'),
});

// Schema de validação para mensagem de mídia
const messageMediaSchema = z.object({
  media_type: z.enum(['image', 'video', 'audio', 'document']),
  media_url: z.string().trim().url('URL inválida').max(2048, 'URL muito longa'),
  caption: z.string().trim().max(1024, 'Legenda muito longa (máximo 1024 caracteres)').optional(),
});

/**
 * Schema para origem "Variável do fluxo".
 * A URL só é conhecida em tempo de execução, portanto validamos apenas que o
 * campo contenha ao menos uma interpolação {{variavel}} (evita URL vazia/inválida).
 */
const messageMediaVariableSchema = z.object({
  media_type: z.enum(['image', 'video', 'audio', 'document']),
  media_url: z
    .string()
    .trim()
    .min(1, 'Informe a variável com a URL do arquivo')
    .max(2048, 'Valor muito longo')
    .refine((v) => /\{\{\s*[\w.]+\s*\}\}/.test(v), 'Informe ao menos uma variável no formato {{variavel}}'),
  caption: z.string().trim().max(1024, 'Legenda muito longa (máximo 1024 caracteres)').optional(),
});


interface ConfigureMessageDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  node: FlowNode | null;
  onSave: (nodeId: string, config: MessageTextConfig | MessageMediaConfig) => void;
  tenantId: string;
}

// Logger para debug
const logger = createLogger('ConfigureMessageDialog');

/**
 * Dialog de configuração de mensagens
 */
export function ConfigureMessageDialog({ open, onOpenChange, node, onSave, tenantId }: ConfigureMessageDialogProps) {
  const isMedia = node?.data.type === 'message_media';
  const nicknameRef = useRef<NodeIdFieldRef>(null);
  
  // Estados do formulário
  const [text, setText] = useState(node?.data.config?.text || '');
  const [mediaType, setMediaType] = useState<'image' | 'video' | 'audio' | 'document'>(
    node?.data.config?.media_type || 'image'
  );
  const [mediaUrl, setMediaUrl] = useState(node?.data.config?.media_url || '');
  const [caption, setCaption] = useState(node?.data.config?.caption || '');
  const [showHelp, setShowHelp] = useState(false);
  
  // Controle de origem da mídia ('variable' adicionado no release 4.3.0)
  const [mediaSource, setMediaSource] = useState<'url' | 'gallery' | 'variable'>('url');
  const [selectedMedia, setSelectedMedia] = useState<MediaGalleryItem | null>(null);
  
  // RELEASE: 4.1.0 - Estados para canal de envio
  const [channelMode, setChannelMode] = useState<'active' | 'specific'>('active');
  const [selectedInboxId, setSelectedInboxId] = useState<string>('');

  /**
   * Query para buscar inboxes ativas do tenant
   * RELEASE: 4.1.0 - Permite selecionar canal específico para envio
   */
  const { data: inboxes = [], isLoading: loadingInboxes } = useQuery({
    queryKey: ['inboxes-selector', tenantId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('inboxes')
        .select('id, name, channel_type, identifier')
        .eq('tenant_id', tenantId)
        .eq('status', 'active')
        .order('name');
      
      if (error) throw error;
      return data || [];
    },
    enabled: !!tenantId && open,
  });

  /**
   * Sincroniza estados quando node mudar
   */
  useEffect(() => {
    if (node) {
      setText(node.data.config?.text || '');
      const cfgMediaType = (node.data.config?.media_type || 'image') as 'image' | 'video' | 'audio' | 'document';
      const cfgMediaUrl = node.data.config?.media_url || '';
      setMediaType(cfgMediaType);
      setMediaUrl(cfgMediaUrl);
      setCaption(node.data.config?.caption || '');

      // RELEASE: 4.2.0 - Restaura contexto da galeria ao reabrir o modal
      // Se a mídia foi salva via galeria, recompõe selectedMedia (objeto leve)
      // para que o card de "Arquivo Selecionado" apareça já na abertura.
      const savedSource = node.data.config?.media_source;
      const galleryId = node.data.config?.media_gallery_id;
      const galleryName = node.data.config?.media_gallery_name;

      if (savedSource === 'gallery' && galleryId) {
        setMediaSource('gallery');
        setSelectedMedia({
          id: galleryId,
          name: galleryName || 'Arquivo da galeria',
          file_type: cfgMediaType,
          storage_path: '',
          size_bytes: 0,
        } as MediaGalleryItem);
      } else if (savedSource === 'variable') {
        // RELEASE: 4.3.0 - Restaura origem por variável do fluxo
        setMediaSource('variable');
        setSelectedMedia(null);
      } else {
        setMediaSource('url');
        setSelectedMedia(null);
      }

      // RELEASE: 4.1.0 - Inicializa canal de envio
      const hasInboxConfig = !!node.data.config?.inbox_id;
      setChannelMode(hasInboxConfig ? 'specific' : 'active');
      setSelectedInboxId(node.data.config?.inbox_id || '');
    }
  }, [node]);

  /**
   * Handler para seleção de mídia da galeria
   * RELEASE: 1.2.0 - Armazena media_gallery_id e media_gallery_name para export/import
   */
  const handleMediaSelect = (media: MediaGalleryItem) => {
    logger.debug('handleMediaSelect chamado', { 
      mediaId: media.id, 
      storagePath: media.storage_path,
      fileType: media.file_type,
      name: media.name
    });
    
    setSelectedMedia(media);
    
    // Preencher automaticamente tipo e URL
    setMediaType(media.file_type as 'image' | 'video' | 'audio' | 'document');
    
    // Gerar URL da mídia
    const url = getStorageUrl(media.storage_path);
    logger.debug('URL gerada via getStorageUrl', { url, storagePath: media.storage_path });
    
    if (url) {
      setMediaUrl(url);
      logger.info('Arquivo da galeria selecionado com sucesso', { 
        name: media.name, 
        url,
        type: media.file_type 
      });
    } else {
      logger.warn('getStorageUrl retornou undefined', { storagePath: media.storage_path });
      toast.error('Erro ao processar arquivo da galeria');
      return;
    }
    
    toast.success(`Arquivo "${media.name}" selecionado`);
  };

  /**
   * Salva a configuração do node
   */
  /**
   * Salva a configuração do node
   * RELEASE: 4.1.0 - Adiciona inbox_id ao config quando canal específico selecionado
   */
  const handleSave = () => {
    if (!node) return;

    try {
      // RELEASE: 4.1.0 - Validação de canal específico
      if (channelMode === 'specific' && !selectedInboxId) {
        toast.error('Selecione uma inbox para envio');
        return;
      }

      if (isMedia) {
        // Validação específica para galeria: verificar se arquivo foi selecionado
        if (mediaSource === 'gallery' && !mediaUrl) {
          logger.warn('Tentativa de salvar sem selecionar arquivo da galeria');
          toast.error('Selecione um arquivo da galeria clicando no botão "Selecionar"');
          return;
        }
        
        // Validação adicional: verificar se URL está vazia
        if (!mediaUrl || mediaUrl.trim() === '') {
          logger.warn('Tentativa de salvar com URL de mídia vazia', { mediaSource });
          toast.error('Informe a URL da mídia, selecione um arquivo da galeria ou escolha uma variável');
          return;
        }
        
        // Valida mídia via Zod
        // RELEASE: 4.3.0 - Origem "variable" usa schema próprio (URL só existe em runtime)
        const baseConfig = (mediaSource === 'variable' ? messageMediaVariableSchema : messageMediaSchema).parse({
          media_type: mediaType,
          media_url: mediaUrl,
          caption: caption || undefined,
        });
        
        // RELEASE: 1.2.0 - Adiciona campos da galeria para export/import
        // Quando origem é galeria, salvar ID e nome para rastreamento
        let config: Record<string, unknown> = { ...baseConfig };
        
        if (mediaSource === 'gallery' && selectedMedia) {
          config.media_source = 'gallery';
          config.media_gallery_id = selectedMedia.id;
          config.media_gallery_name = selectedMedia.name;
        } else if (mediaSource === 'variable') {
          config.media_source = 'variable';
        } else {
          config.media_source = 'url';
        }

        
        // RELEASE: 4.1.0 - Adiciona inbox_id se canal específico
        if (channelMode === 'specific' && selectedInboxId) {
          config.inbox_id = selectedInboxId;
        }
        
        logger.info('Configuração de mídia salva', { 
          mediaType, 
          hasCaption: !!caption, 
          mediaSource, 
          channelMode,
          hasGalleryId: !!config.media_gallery_id,
        });
        onSave(node.id, { ...config, nickname: nicknameRef.current?.getNickname() } as unknown as MessageMediaConfig);
      } else {
        // Valida texto
        const baseConfig = messageTextSchema.parse({ text });
        
        // RELEASE: 4.1.0 - Adiciona inbox_id se canal específico
        const config = channelMode === 'specific' && selectedInboxId
          ? { ...baseConfig, inbox_id: selectedInboxId }
          : baseConfig;
        
        logger.info('Configuração de texto salva', { textLength: text.length, channelMode });
        onSave(node.id, { ...config, nickname: nicknameRef.current?.getNickname() } as any);
      }
      
      toast.success('Configuração salva com sucesso');
      onOpenChange(false);
    } catch (error) {
      if (error instanceof z.ZodError) {
        const firstError = error.errors[0];
        logger.error('Erro de validação Zod', { error: firstError.message });
        toast.error(firstError.message);
      } else {
        logger.error('Erro ao salvar configuração', { error });
        toast.error('Erro ao salvar configuração');
      }
    }
  };

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <div className="flex items-center justify-between">
              <DialogTitle>
                {isMedia ? 'Configurar Envio de Mídia' : 'Configurar Mensagem de Texto'}
              </DialogTitle>
              <Button variant="ghost" size="icon" onClick={() => setShowHelp(true)}>
                <HelpCircle className="h-5 w-5" />
              </Button>
            </div>
          </DialogHeader>

        <div className="space-y-4 py-4">
          {/* Campo ID do Node (somente leitura) */}
          <NodeIdField ref={nicknameRef} nodeId={node?.id} defaultNickname={node?.data.config?.nickname} />

          {/* RELEASE: 4.1.0 - Seção Canal de Envio */}
          <Separator className="my-2" />
          <div className="space-y-3">
            <div className="flex items-center gap-2">
              <Send className="h-4 w-4 text-muted-foreground" />
              <Label className="text-sm font-medium">Canal de Envio</Label>
            </div>
            <RadioGroup
              value={channelMode}
              onValueChange={(v: 'active' | 'specific') => setChannelMode(v)}
              className="space-y-2"
            >
              <div className="flex items-start space-x-3 p-3 rounded-lg border hover:bg-accent/50 cursor-pointer">
                <RadioGroupItem value="active" id="channel-active" className="mt-0.5" />
                <div className="space-y-1 flex-1">
                  <Label htmlFor="channel-active" className="font-medium cursor-pointer">
                    Canal da conversa ativa
                  </Label>
                  <p className="text-xs text-muted-foreground">
                    Usa o canal definido pelo gatilho do fluxo (padrão para gatilhos de chat)
                  </p>
                </div>
              </div>
              
              <div className="flex items-start space-x-3 p-3 rounded-lg border hover:bg-accent/50 cursor-pointer">
                <RadioGroupItem value="specific" id="channel-specific" className="mt-0.5" />
                <div className="space-y-1 flex-1">
                  <Label htmlFor="channel-specific" className="font-medium cursor-pointer">
                    Canal específico
                  </Label>
                  <p className="text-xs text-muted-foreground">
                    Selecione manualmente o canal (obrigatório para gatilhos CRM)
                  </p>
                </div>
              </div>
            </RadioGroup>

            {/* Seletor de Inbox (apenas quando modo específico) */}
            {channelMode === 'specific' && (
              <div className="space-y-2 pl-4 border-l-2 border-primary/30">
                <Label>Inbox para Envio *</Label>
                <Select 
                  value={selectedInboxId} 
                  onValueChange={setSelectedInboxId}
                  disabled={loadingInboxes}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Selecione uma inbox..." />
                  </SelectTrigger>
                  <SelectContent>
                    {inboxes.map((inbox) => (
                      <SelectItem key={inbox.id} value={inbox.id}>
                        <div className="flex items-center gap-2">
                          <MessageSquare className="h-3 w-3" />
                          <span>{inbox.name}</span>
                          <span className="text-xs text-muted-foreground">
                            ({inbox.channel_type})
                          </span>
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground">
                  A mensagem será enviada através deste canal
                </p>
              </div>
            )}
          </div>
          <Separator className="my-2" />

          {isMedia ? (
            <>
              {/* Origem da mídia */}
              <div className="space-y-3">
                <Label>Origem da Mídia</Label>
                <RadioGroup
                  value={mediaSource}
                  onValueChange={(v: 'url' | 'gallery' | 'variable') => setMediaSource(v)}
                  className="flex flex-wrap gap-4"
                >
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="url" id="source-url" />
                    <Label htmlFor="source-url" className="font-normal flex items-center gap-2 cursor-pointer">
                      <Link className="h-4 w-4" />
                      URL Externa
                    </Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="gallery" id="source-gallery" />
                    <Label htmlFor="source-gallery" className="font-normal flex items-center gap-2 cursor-pointer">
                      <Image className="h-4 w-4" />
                      Galeria de Mídia
                    </Label>
                  </div>
                  {/* RELEASE: 4.3.0 - Nova origem: variável interpolada do fluxo */}
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="variable" id="source-variable" />
                    <Label htmlFor="source-variable" className="font-normal flex items-center gap-2 cursor-pointer">
                      <Braces className="h-4 w-4" />
                      Variável do fluxo
                    </Label>
                  </div>
                </RadioGroup>
              </div>

              {mediaSource === 'variable' ? (
                <>
                  {/* RELEASE: 4.3.0 - Arquivo vindo de outro node (ex.: Conexão) */}
                  <div className="space-y-2">
                    <Label>Tipo de Mídia</Label>
                    <Select value={mediaType} onValueChange={(v: any) => setMediaType(v)}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="image">Imagem</SelectItem>
                        <SelectItem value="video">Vídeo</SelectItem>
                        <SelectItem value="audio">Áudio</SelectItem>
                        <SelectItem value="document">Documento</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label>Variável com a URL do arquivo *</Label>
                    <InputWithVariables
                      tenantId={tenantId}
                      value={mediaUrl}
                      onChange={setMediaUrl}
                      placeholder="{{consulta_pdf_url}}"
                      maxLength={2048}
                    />
                    <p className="text-xs text-muted-foreground">
                      A URL é resolvida em tempo de execução. Ex.: o node Conexão publica
                      <code className="mx-1">{'{{variavel_pdf_url}}'}</code> com o PDF retornado pelo provider.
                    </p>
                  </div>
                </>
              ) : mediaSource === 'url' ? (

                <>
                  <div className="space-y-2">
                    <Label>Tipo de Mídia</Label>
                    <Select value={mediaType} onValueChange={(v: any) => setMediaType(v)}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="image">Imagem</SelectItem>
                        <SelectItem value="video">Vídeo</SelectItem>
                        <SelectItem value="audio">Áudio</SelectItem>
                        <SelectItem value="document">Documento</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label>URL da Mídia *</Label>
                    <Input
                      placeholder="https://exemplo.com/arquivo.jpg"
                      value={mediaUrl}
                      onChange={(e) => setMediaUrl(e.target.value)}
                      maxLength={2048}
                    />
                    <p className="text-xs text-muted-foreground">
                      URL pública acessível do arquivo de mídia
                    </p>

                    {/* RELEASE: 4.2.0 - Preview universal da mídia informada via URL */}
                    {mediaUrl && (
                      <MediaConfigPreview
                        mediaType={mediaType}
                        mediaUrl={mediaUrl}
                        fileName={mediaUrl.split('/').pop() || 'Mídia'}
                      />
                    )}
                  </div>
                </>
              ) : (
                <>
                  {/* Preview do arquivo selecionado ou Picker da galeria */}
                  {selectedMedia ? (
                    <div className="space-y-2">
                      <Label>Arquivo Selecionado</Label>
                      <div className="flex items-center gap-3 p-3 border rounded-lg bg-muted/50">
                        <div className="text-2xl">
                          {selectedMedia.file_type === 'image' && '🖼️'}
                          {selectedMedia.file_type === 'video' && '🎥'}
                          {selectedMedia.file_type === 'audio' && '🎵'}
                          {selectedMedia.file_type === 'document' && '📄'}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium truncate">{selectedMedia.name}</p>
                          <p className="text-xs text-muted-foreground">
                            {selectedMedia.file_type.toUpperCase()} • {(selectedMedia.size_bytes / 1024).toFixed(0)} KB
                          </p>
                        </div>
                        <Button 
                          variant="ghost" 
                          size="icon"
                          onClick={() => {
                            setSelectedMedia(null);
                            setMediaUrl('');
                            logger.debug('Arquivo da galeria removido');
                          }}
                        >
                          <X className="h-4 w-4" />
                        </Button>
                      </div>
                      <p className="text-xs text-muted-foreground">
                        ✓ Arquivo confirmado. Clique no X para selecionar outro.
                      </p>

                      {/* RELEASE: 4.2.0 - Preview universal da mídia da galeria */}
                      {mediaUrl && (
                        <MediaConfigPreview
                          mediaType={selectedMedia.file_type as 'image' | 'video' | 'audio' | 'document'}
                          mediaUrl={mediaUrl}
                          fileName={selectedMedia.name}
                        />
                      )}

                      {/* Aviso de limite 16MB da Meta WABA - apenas informativo */}
                      {selectedMedia.size_bytes > 16 * 1024 * 1024 && (
                        <div className="flex gap-2 p-3 rounded-md bg-amber-50 border border-amber-200 text-amber-800 dark:bg-amber-950/30 dark:border-amber-800 dark:text-amber-300">
                          <AlertTriangle className="h-5 w-5 shrink-0 mt-0.5" />
                          <p className="text-sm">
                            Este arquivo tem <strong>{formatBytes(selectedMedia.size_bytes)}</strong> e ultrapassa o limite de <strong>16 MB</strong> do WhatsApp Oficial (Meta WABA). 
                            Se você estiver usando uma API não-oficial, este limite não se aplica.
                          </p>
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="border rounded-lg p-4">
                      <MediaGalleryPicker
                        onSelect={handleMediaSelect}
                        onCancel={() => setMediaSource('url')}
                        allowedTypes={['image', 'video', 'audio', 'document']}
                        currentSelection={selectedMedia?.id}
                      />
                    </div>
                  )}
                </>
              )}

              <div className="space-y-2">
                <Label>Legenda (opcional)</Label>
                <TextareaWithVariables
                  tenantId={tenantId}
                  value={caption}
                  onChange={setCaption}
                  placeholder="Digite a legenda da mídia... Use {{variavel}} para inserir variáveis"
                  maxLength={1024}
                  rows={3}
                />
                <p className="text-xs text-muted-foreground">
                  {caption.length}/1024 caracteres
                </p>
              </div>
            </>
          ) : (
            <div className="space-y-2">
              <Label>Texto da Mensagem *</Label>
              <TextareaWithVariables
                tenantId={tenantId}
                value={text}
                onChange={setText}
                placeholder="Digite o texto da mensagem... Use {{variavel}} para inserir variáveis"
                maxLength={4096}
                rows={6}
              />
              <p className="text-xs text-muted-foreground">
                {text.length}/4096 caracteres
              </p>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancelar
          </Button>
          <Button onClick={handleSave}>
            Salvar Configuração
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>

    <NodeHelpDialog 
      open={showHelp} 
      onOpenChange={setShowHelp} 
      nodeType={isMedia ? 'message_media' : 'message_text'} 
    />
    </>
  );
}
